import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ChevronRight, ChevronLeft, Truck, Shield, Star, ArrowRight, Phone, Sparkles, Award, Clock, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import ProductCard from '../components/ProductCard';
import { ScrollReveal } from '../hooks/useScrollAnimation';
import PromoBanners from '../components/PromoBanners';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Turkish sweets background images - User's custom images
const SWEETS = {
    // Main hero background - User's baklava image
    heroBackground: "https://customer-assets.emergentagent.com/job_taste-turkey-1/artifacts/b7snobfm_statia-aptel-baklava-600x450.jpg",
    // User's product images
    pismaniye: "https://customer-assets.emergentagent.com/job_taste-turkey-1/artifacts/6k7l13i8_ChatGPT%20Image%20Mar%2019%2C%202026%2C%2002_51_10%20PM.png",
    baklavaGold: "https://customer-assets.emergentagent.com/job_taste-turkey-1/artifacts/2n3ihozq_ChatGPT%20Image%20Mar%2019%2C%202026%2C%2003_14_44%20PM.png",
    cheese: "https://customer-assets.emergentagent.com/job_taste-turkey-1/artifacts/3y3ux11t_ChatGPT%20Image%20Mar%2019%2C%202026%2C%2003_16_01%20PM.png",
    oliveOil: "https://customer-assets.emergentagent.com/job_taste-turkey-1/artifacts/1kpttuzx_ChatGPT%20Image%20Mar%2019%2C%202026%2C%2003_19_23%20PM.png",
    chocolateBaklava: "https://customer-assets.emergentagent.com/job_taste-turkey-1/artifacts/2kfkvfet_ChatGPT%20Image%20Mar%2019%2C%202026%2C%2003_23_56%20PM.png",
    rice: "https://customer-assets.emergentagent.com/job_taste-turkey-1/artifacts/rybpklk8_ChatGPT%20Image%20Mar%2019%2C%202026%2C%2001_24_47%20PM.png",
    sucuk: "https://customer-assets.emergentagent.com/job_taste-turkey-1/artifacts/739dzwf1_ChatGPT%20Image%20Mar%2019%2C%202026%2C%2002_34_45%20PM.png",
    churchkhelaChoco: "https://customer-assets.emergentagent.com/job_taste-turkey-1/artifacts/immrecfe_ChatGPT%20Image%20Mar%2019%2C%202026%2C%2002_42_34%20PM.png",
    churchkhelaRed: "https://customer-assets.emergentagent.com/job_taste-turkey-1/artifacts/34spjguy_ChatGPT%20Image%20Mar%2019%2C%202026%2C%2002_43_58%20PM.png",
    stuffedPeppers: "https://customer-assets.emergentagent.com/job_taste-turkey-1/artifacts/clnlwjtf_ChatGPT%20Image%20Mar%2019%2C%202026%2C%2002_48_18%20PM.png",
    stringCheese: "https://customer-assets.emergentagent.com/job_taste-turkey-1/artifacts/dapqdrhb_ChatGPT%20Image%20Mar%2019%2C%202026%2C%2012_43_33%20PM.png",
    greenOlives: "https://customer-assets.emergentagent.com/job_taste-turkey-1/artifacts/zzwhh6q8_ChatGPT%20Image%20Mar%2019%2C%202026%2C%2012_56_28%20PM.png",
    blackOlives: "https://customer-assets.emergentagent.com/job_taste-turkey-1/artifacts/l1wm43qt_ChatGPT%20Image%20Mar%2019%2C%202026%2C%2012_58_18%20PM.png",
    lokumMix: "https://customer-assets.emergentagent.com/job_taste-turkey-1/artifacts/hdgr7ewg_home~Rf3nbb~iJ9wYW_j.jpg",
    lokumPistachio: "https://customer-assets.emergentagent.com/job_taste-turkey-1/artifacts/1qrqeomc_ChatGPT%20Image%20Mar%2019%2C%202026%2C%2001_06_01%20PM.png",
    lokumBox: "https://customer-assets.emergentagent.com/job_taste-turkey-1/artifacts/e3wn6cwm_IMG_20210420_224041.jpg",
};

const HomePage = () => {
    const [featuredProducts, setFeaturedProducts] = useState([]);
    const [bestsellers, setBestsellers] = useState([]);
    const [onSale, setOnSale] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const controller = new AbortController();
        
        const fetchData = async () => {
            try {
                // Fetch all data in parallel with abort controller
                const [featuredRes, bestRes, saleRes, catRes] = await Promise.all([
                    axios.get(`${API}/products`, { 
                        params: { featured: true, limit: 4 },
                        signal: controller.signal 
                    }),
                    axios.get(`${API}/products`, { 
                        params: { bestseller: true, limit: 4 },
                        signal: controller.signal 
                    }),
                    axios.get(`${API}/products`, { 
                        params: { on_sale: true, limit: 4 },
                        signal: controller.signal 
                    }),
                    axios.get(`${API}/categories`, { signal: controller.signal })
                ]);
                
                if (!controller.signal.aborted) {
                    setFeaturedProducts(featuredRes.data.products || []);
                    setBestsellers(bestRes.data.products || []);
                    setOnSale(saleRes.data.products || []);
                    setCategories(catRes.data || []);
                }
            } catch (error) {
                if (error.name !== 'CanceledError' && error.name !== 'AbortError') {
                    console.error('Error fetching data:', error);
                }
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            }
        };
        
        fetchData();
        return () => controller.abort();
    }, []);

    return (
        <div className="min-h-screen relative">
            {/* ===== FIXED FULL PAGE BACKGROUND ===== */}
            <div 
                className="fixed inset-0 bg-cover bg-center bg-no-repeat z-0"
                style={{ 
                    backgroundImage: `url(${SWEETS.heroBackground})`
                }}
            />
            {/* Dark overlay for entire page */}
            <div className="fixed inset-0 bg-black/40 z-0" />
            
            {/* Content wrapper */}
            <div className="relative z-10">
            {/* ===== HERO SECTION ===== */}
            <section className="relative min-h-[100vh] overflow-hidden">
                {/* Additional hero overlay for better contrast */}
                <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/40" />
                
                {/* Warm glow effects */}
                <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-amber-500/15 rounded-full blur-[150px]" />
                <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-orange-500/15 rounded-full blur-[150px]" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-green-400/5 rounded-full blur-[200px]" />

                {/* ===== LEFT SIDE FLOATING SWEETS ===== */}
                <div className="absolute left-0 top-0 bottom-0 w-[35%] hidden lg:block pointer-events-none overflow-hidden">
                    {/* Row 1 - Top */}
                    <div className="absolute top-12 left-4 animate-float" style={{ animationDelay: '0s', animationDuration: '6s' }}>
                        <div className="relative group">
                            <div className="absolute inset-0 bg-amber-400/40 rounded-2xl blur-2xl scale-110" />
                            <img src={SWEETS.baklavaGold} alt="Баклава" className="w-44 h-44 object-cover rounded-2xl shadow-2xl ring-4 ring-amber-400/50 hover:scale-105 transition-transform" />
                        </div>
                    </div>
                    
                    <div className="absolute top-8 left-52 animate-float-slow" style={{ animationDelay: '0.5s', animationDuration: '7s' }}>
                        <div className="relative">
                            <div className="absolute inset-0 bg-rose-400/40 rounded-2xl blur-2xl scale-110" />
                            <img src={SWEETS.lokumBox} alt="Локум" className="w-32 h-32 object-cover rounded-2xl shadow-2xl ring-4 ring-rose-400/50" />
                        </div>
                    </div>

                    {/* Row 2 */}
                    <div className="absolute top-[28%] left-8 animate-float-slow" style={{ animationDelay: '1s', animationDuration: '8s' }}>
                        <div className="relative">
                            <div className="absolute inset-0 bg-pink-400/40 rounded-2xl blur-2xl scale-110" />
                            <img src={SWEETS.lokumMix} alt="Локум" className="w-52 h-52 object-cover rounded-2xl shadow-2xl ring-4 ring-pink-400/50" />
                        </div>
                    </div>
                    
                    <div className="absolute top-[32%] left-64 animate-float" style={{ animationDelay: '1.5s', animationDuration: '6.5s' }}>
                        <img src={SWEETS.stringCheese} alt="Сирене" className="w-28 h-28 object-cover rounded-2xl shadow-2xl ring-4 ring-green-300/50" />
                    </div>

                    {/* Row 3 - Middle */}
                    <div className="absolute top-[50%] left-4 animate-float" style={{ animationDelay: '2s', animationDuration: '7s' }}>
                        <div className="relative">
                            <div className="absolute inset-0 bg-green-400/40 rounded-2xl blur-2xl scale-110" />
                            <img src={SWEETS.oliveOil} alt="Зехтин" className="w-40 h-40 object-cover rounded-2xl shadow-2xl ring-4 ring-green-400/50" />
                        </div>
                    </div>
                    
                    <div className="absolute top-[55%] left-48 animate-float-slow" style={{ animationDelay: '2.5s', animationDuration: '8s' }}>
                        <img src={SWEETS.churchkhelaRed} alt="Чурчхела" className="w-36 h-36 object-cover rounded-2xl shadow-2xl ring-4 ring-violet-400/50" />
                    </div>

                    {/* Row 4 - Bottom */}
                    <div className="absolute top-[72%] left-12 animate-float-slow" style={{ animationDelay: '3s', animationDuration: '7.5s' }}>
                        <div className="relative">
                            <div className="absolute inset-0 bg-yellow-400/40 rounded-2xl blur-2xl scale-110" />
                            <img src={SWEETS.pismaniye} alt="Пишманье" className="w-44 h-44 object-cover rounded-2xl shadow-2xl ring-4 ring-yellow-400/50" />
                        </div>
                    </div>
                    
                    <div className="absolute top-[78%] left-60 animate-float" style={{ animationDelay: '3.5s', animationDuration: '6s' }}>
                        <img src={SWEETS.greenOlives} alt="Маслини" className="w-32 h-32 object-cover rounded-2xl shadow-2xl ring-4 ring-green-400/50" />
                    </div>
                    
                    <div className="absolute bottom-8 left-4 animate-float" style={{ animationDelay: '4s', animationDuration: '7s' }}>
                        <img src={SWEETS.rice} alt="Ориз" className="w-28 h-28 object-cover rounded-2xl shadow-2xl ring-4 ring-amber-300/50" />
                    </div>
                </div>

                {/* ===== RIGHT SIDE FLOATING SWEETS ===== */}
                <div className="absolute right-0 top-0 bottom-0 w-[35%] hidden lg:block pointer-events-none overflow-hidden">
                    {/* Row 1 - Top */}
                    <div className="absolute top-10 right-8 animate-float-slow" style={{ animationDelay: '0.3s', animationDuration: '7s' }}>
                        <div className="relative group">
                            <div className="absolute inset-0 bg-orange-400/40 rounded-2xl blur-2xl scale-110" />
                            <img src={SWEETS.chocolateBaklava} alt="Шоколадова баклава" className="w-48 h-48 object-cover rounded-2xl shadow-2xl ring-4 ring-orange-400/50 hover:scale-105 transition-transform" />
                        </div>
                    </div>
                    
                    <div className="absolute top-16 right-60 animate-float" style={{ animationDelay: '0.8s', animationDuration: '6s' }}>
                        <img src={SWEETS.cheese} alt="Сирене" className="w-32 h-32 object-cover rounded-2xl shadow-2xl ring-4 ring-blue-400/50" />
                    </div>

                    {/* Row 2 */}
                    <div className="absolute top-[26%] right-4 animate-float" style={{ animationDelay: '1.2s', animationDuration: '6.5s' }}>
                        <div className="relative">
                            <div className="absolute inset-0 bg-violet-400/40 rounded-2xl blur-2xl scale-110" />
                            <img src={SWEETS.lokumPistachio} alt="Локум с шамфъстък" className="w-52 h-52 object-cover rounded-2xl shadow-2xl ring-4 ring-violet-400/50" />
                        </div>
                    </div>
                    
                    <div className="absolute top-[30%] right-56 animate-float-slow" style={{ animationDelay: '1.8s', animationDuration: '8s' }}>
                        <img src={SWEETS.blackOlives} alt="Черни маслини" className="w-28 h-28 object-cover rounded-2xl shadow-2xl ring-4 ring-slate-400/50" />
                    </div>

                    {/* Row 3 - Middle */}
                    <div className="absolute top-[48%] right-8 animate-float-slow" style={{ animationDelay: '2.2s', animationDuration: '7.5s' }}>
                        <div className="relative">
                            <div className="absolute inset-0 bg-red-400/40 rounded-2xl blur-2xl scale-110" />
                            <img src={SWEETS.sucuk} alt="Суджук" className="w-40 h-40 object-cover rounded-2xl shadow-2xl ring-4 ring-red-400/50" />
                        </div>
                    </div>
                    
                    <div className="absolute top-[52%] right-52 animate-float" style={{ animationDelay: '2.7s', animationDuration: '6s' }}>
                        <div className="relative">
                            <div className="absolute inset-0 bg-amber-400/40 rounded-2xl blur-2xl scale-110" />
                            <img src={SWEETS.churchkhelaChoco} alt="Суджук шоколад" className="w-36 h-36 object-cover rounded-2xl shadow-2xl ring-4 ring-amber-400/50" />
                        </div>
                    </div>

                    {/* Row 4 - Bottom */}
                    <div className="absolute top-[70%] right-12 animate-float" style={{ animationDelay: '3.2s', animationDuration: '7s' }}>
                        <div className="relative">
                            <div className="absolute inset-0 bg-emerald-400/40 rounded-2xl blur-2xl scale-110" />
                            <img src={SWEETS.stuffedPeppers} alt="Пълнени чушки" className="w-44 h-44 object-cover rounded-2xl shadow-2xl ring-4 ring-emerald-400/50" />
                        </div>
                    </div>
                    
                    <div className="absolute top-[76%] right-60 animate-float-slow" style={{ animationDelay: '3.7s', animationDuration: '8s' }}>
                        <img src={SWEETS.lokumBox} alt="Локум кутия" className="w-32 h-32 object-cover rounded-2xl shadow-2xl ring-4 ring-yellow-400/50" />
                    </div>
                    
                    <div className="absolute bottom-12 right-6 animate-float" style={{ animationDelay: '4.2s', animationDuration: '6.5s' }}>
                        <img src={SWEETS.baklavaGold} alt="Баклава" className="w-28 h-28 object-cover rounded-2xl shadow-2xl ring-4 ring-amber-300/50" />
                    </div>
                </div>

                {/* ===== CENTER CONTENT ===== */}
                <div className="container-main relative z-10 min-h-[100vh] flex flex-col items-center justify-center py-20">
                    <div className="text-center max-w-3xl">
                        {/* Animated sparkle badge */}
                        <div className="inline-flex items-center gap-3 mb-8 px-8 py-4 rounded-full bg-gradient-to-r from-amber-500/20 to-orange-500/20 backdrop-blur-md border border-amber-400/30 shadow-2xl animate-pulse-slow">
                            <Sparkles className="w-6 h-6 text-amber-400 animate-spin-slow" />
                            <span className="text-amber-100 text-sm md:text-base font-bold uppercase tracking-widest">
                                Автентичен Вкус от Турция
                            </span>
                            <Sparkles className="w-6 h-6 text-amber-400 animate-spin-slow" style={{ animationDirection: 'reverse' }} />
                        </div>

                        {/* Main Title with glow and side effects */}
                        <div className="relative">
                            {/* Left decorative elements */}
                            <div className="absolute -left-20 top-1/2 -translate-y-1/2 hidden xl:flex flex-col items-center gap-3">
                                <div className="w-16 h-0.5 bg-gradient-to-r from-transparent to-amber-400 rounded-full" />
                                <div className="w-3 h-3 bg-amber-400 rounded-full animate-pulse shadow-lg shadow-amber-400/50" />
                                <div className="w-12 h-0.5 bg-gradient-to-r from-transparent to-amber-300 rounded-full" />
                                <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse" style={{ animationDelay: '0.5s' }} />
                                <div className="w-8 h-0.5 bg-gradient-to-r from-transparent to-amber-400 rounded-full" />
                            </div>
                            
                            {/* Right decorative elements */}
                            <div className="absolute -right-20 top-1/2 -translate-y-1/2 hidden xl:flex flex-col items-center gap-3">
                                <div className="w-16 h-0.5 bg-gradient-to-l from-transparent to-amber-400 rounded-full" />
                                <div className="w-3 h-3 bg-amber-400 rounded-full animate-pulse shadow-lg shadow-amber-400/50" />
                                <div className="w-12 h-0.5 bg-gradient-to-l from-transparent to-amber-300 rounded-full" />
                                <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse" style={{ animationDelay: '0.5s' }} />
                                <div className="w-8 h-0.5 bg-gradient-to-l from-transparent to-amber-400 rounded-full" />
                            </div>

                            {/* Glow effect behind text */}
                            <div className="absolute inset-0 blur-3xl bg-gradient-to-r from-amber-500/20 via-orange-500/30 to-amber-500/20 scale-150" />
                            
                            <h1 className="relative text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold mb-6 tracking-tight">
                                <span className="text-white drop-shadow-[0_0_30px_rgba(255,255,255,0.3)]">Вкусът на</span>
                                <br />
                                <span className="relative inline-block">
                                    {/* Animated underline */}
                                    <span className="absolute -bottom-2 left-0 right-0 h-2 bg-gradient-to-r from-transparent via-amber-400 to-transparent rounded-full animate-pulse" />
                                    {/* Main text with shimmer */}
                                    <span className="bg-gradient-to-r from-amber-300 via-yellow-400 to-orange-400 bg-clip-text text-transparent drop-shadow-[0_0_40px_rgba(251,191,36,0.5)] animate-shimmer bg-[length:200%_auto]">
                                        ТУРЦИЯ
                                    </span>
                                    {/* Sparkle effects */}
                                    <span className="absolute -top-4 -left-4 text-amber-300 animate-ping">✦</span>
                                    <span className="absolute -top-2 -right-6 text-orange-300 animate-pulse">✧</span>
                                    <span className="absolute -bottom-4 -right-4 text-yellow-300 animate-ping" style={{ animationDelay: '0.3s' }}>✦</span>
                                </span>
                            </h1>
                        </div>

                        {/* Decorative divider */}
                        <div className="flex items-center justify-center gap-4 mb-8">
                            <div className="w-16 h-1 rounded-full bg-gradient-to-r from-transparent to-amber-400" />
                            <Star className="w-6 h-6 text-amber-400 fill-amber-400" />
                            <div className="w-24 h-1.5 rounded-full bg-gradient-to-r from-amber-400 via-orange-400 to-amber-400 shadow-lg shadow-amber-500/50" />
                            <Star className="w-6 h-6 text-amber-400 fill-amber-400" />
                            <div className="w-16 h-1 rounded-full bg-gradient-to-l from-transparent to-amber-400" />
                        </div>

                        {/* Subtitle */}
                        <p className="text-white/90 text-lg md:text-xl lg:text-2xl mb-12 leading-relaxed max-w-2xl mx-auto font-medium drop-shadow-lg">
                            <span className="text-amber-300 font-bold">Баклава</span>, <span className="text-pink-300 font-bold">локум</span>, <span className="text-rose-300 font-bold">пишмание</span> и още изискани турски деликатеси — доставени директно до вашата врата.
                        </p>

                        {/* Free Delivery Badge - Animated */}
                        <div className="mb-10">
                            <div className="inline-flex items-center gap-4 px-10 py-5 bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 rounded-2xl shadow-2xl shadow-amber-500/40 hover:shadow-amber-500/60 transition-all hover:scale-105 cursor-pointer animate-shimmer bg-[length:200%_auto]">
                                <Truck className="h-7 w-7 text-white" />
                                <p className="text-white font-bold text-xl">
                                    Безплатна доставка за София над <span className="text-3xl">25€</span>
                                </p>
                            </div>
                        </div>

                        {/* CTA Buttons */}
                        <div className="flex flex-col sm:flex-row gap-5 justify-center mb-12">
                            <Link to="/products?category=cat_a7e5ae600638">
                                <Button data-testid="hero-food-btn" className="bg-white hover:bg-amber-50 text-amber-700 text-lg px-12 py-6 shadow-2xl hover:shadow-amber-200/50 transition-all hover:scale-105 font-bold rounded-xl">
                                    Хранителни продукти
                                    <ArrowRight className="ml-2 h-6 w-6" />
                                </Button>
                            </Link>
                            <Link to="/products?category=cat_0538d903de81">
                                <Button data-testid="hero-cleaning-btn" className="bg-transparent border-2 border-white/50 text-white hover:bg-white/10 hover:border-white text-lg px-12 py-6 transition-all hover:scale-105 font-bold rounded-xl">
                                    Перилни препарати
                                </Button>
                            </Link>
                        </div>

                        {/* Contact Card */}
                        <div className="inline-flex items-center gap-5 bg-white/10 backdrop-blur-md px-10 py-5 rounded-2xl border border-white/20 hover:bg-white/15 transition-all cursor-pointer group">
                            <div className="w-16 h-16 rounded-2xl flex items-center justify-center bg-gradient-to-br from-amber-400 to-orange-500 shadow-lg group-hover:scale-110 transition-transform">
                                <Phone className="h-7 w-7 text-white" />
                            </div>
                            <div className="text-left">
                                <p className="text-white/70 text-sm font-medium">Поръчки по телефон</p>
                                <a href="tel:0899972727" className="text-3xl font-bold text-white hover:text-amber-300 transition-colors">
                                    0899 97 27 27
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bottom fade */}
                <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-amber-50 dark:from-slate-900 to-transparent" />

                {/* Scroll Indicator */}
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
                    <div className="w-7 h-11 border-2 border-amber-300 rounded-full flex justify-center">
                        <div className="w-1.5 h-3 bg-amber-500 rounded-full mt-2 animate-bounce" />
                    </div>
                </div>
            </section>

            {/* ===== PROMO BANNERS (hero + secondary) ===== */}
            <section className="container-main pt-8 space-y-6">
                <PromoBanners placement="hero" />
                <PromoBanners placement="secondary" />
            </section>

            {/* ===== FEATURES BAR ===== */}
            <ScrollReveal animation="fade-up">
                <section className="py-10 bg-white/85 dark:bg-slate-800/85 backdrop-blur-[2px] border-y border-amber-200/50 dark:border-slate-700">
                    <div className="container-main">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                            {[
                                { icon: Truck, title: 'Бърза доставка', desc: 'С Еконт до 2-3 дни', color: 'amber' },
                                { icon: Shield, title: 'Гарантирано качество', desc: 'Директен внос', color: 'orange' },
                                { icon: Award, title: 'Премиум селекция', desc: 'Над 10,000 продукта', color: 'amber' },
                                { icon: Clock, title: 'Работно време', desc: 'Пон-Пет 08:30-20:00 | Съб-Нед 09:00-20:00', color: 'orange' }
                            ].map((feature) => (
                                <div key={feature.title} className="flex items-center gap-4 justify-center md:justify-start">
                                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center bg-gradient-to-br from-${feature.color}-400 to-${feature.color}-500 shadow-lg`}>
                                        <feature.icon className="h-6 w-6 text-white" />
                                    </div>
                                    <div>
                                        <p className="font-semibold text-slate-800 dark:text-white">{feature.title}</p>
                                        <p className="text-sm text-slate-500 dark:text-slate-400">{feature.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            </ScrollReveal>

            {/* ===== CATEGORIES with Sweets Background ===== */}
            <section className="section-spacing relative overflow-hidden">
                {/* Glass morphism background - shows baklava through */}
                <div className="absolute inset-0 bg-white/80 dark:bg-slate-900/80 backdrop-blur-[2px]" />
                
                <div className="container-main relative z-10">
                    <ScrollReveal animation="fade-up">
                        <div className="text-center mb-16">
                            <span className="inline-flex items-center gap-2 px-6 py-2 rounded-full bg-white/20 backdrop-blur-sm text-white uppercase tracking-[0.2em] text-sm font-bold mb-4" style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}>
                                <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                                Категории
                                <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                            </span>
                            <h2 className="text-4xl md:text-5xl font-bold mt-4" style={{ color: '#ffffff', textShadow: '2px 2px 4px rgba(0,0,0,0.7)' }}>
                                Нашите колекции
                            </h2>
                            <div className="w-32 h-1.5 mx-auto mt-6 rounded-full bg-gradient-to-r from-amber-400 via-orange-400 to-amber-400" />
                        </div>
                    </ScrollReveal>
                    
                    {/* Category Grid - Dynamic from API */}
                    <div className={`grid grid-cols-1 ${categories.length >= 2 ? 'lg:grid-cols-2' : ''} ${categories.length >= 3 ? 'lg:grid-cols-3' : ''} gap-8`}>
                        {categories.map((category, index) => {
                            // Define colors for each category - warm orange/amber theme
                            const categoryStyles = {
                                'cat_a7e5ae600638': { // Хранителни продукти
                                    gradient: 'from-amber-500 to-orange-500', 
                                    overlay: 'from-amber-900/90 via-amber-800/60', 
                                    shadow: 'hover:shadow-amber-500/30',
                                    badge: 'Продукти'
                                },
                                'cat_22b1ec9748cf': { // Турски сладки и деликатеси
                                    gradient: 'from-orange-500 to-red-500', 
                                    overlay: 'from-orange-900/90 via-orange-800/60', 
                                    shadow: 'hover:shadow-orange-500/30',
                                    badge: 'Деликатеси'
                                },
                                'cat_80efdcff717b': { // Основни продукти
                                    gradient: 'from-yellow-500 to-amber-500', 
                                    overlay: 'from-yellow-900/90 via-amber-800/60', 
                                    shadow: 'hover:shadow-yellow-500/30',
                                    badge: 'Продукти'
                                },
                                'cat_0538d903de81': { // Перилни препарати
                                    gradient: 'from-teal-500 to-cyan-500', 
                                    overlay: 'from-teal-900/90 via-teal-800/60', 
                                    shadow: 'hover:shadow-teal-500/30',
                                    badge: 'За дома'
                                },
                                'cat_61467c2f332f': { // Напитки
                                    gradient: 'from-rose-500 to-pink-500', 
                                    overlay: 'from-rose-900/90 via-rose-800/60', 
                                    shadow: 'hover:shadow-rose-500/30',
                                    badge: 'Напитки'
                                },
                                'default': { 
                                    gradient: 'from-amber-500 to-orange-600', 
                                    overlay: 'from-amber-900/90 via-orange-800/60', 
                                    shadow: 'hover:shadow-amber-500/30',
                                    badge: 'Продукти'
                                }
                            };
                            
                            const style = categoryStyles[category.category_id] || categoryStyles['default'];
                            // Custom fallback background images per category (used when category.image_url is empty)
                            const categoryImageFallbacks = {
                                'cat_a7e5ae600638': 'https://customer-assets.emergentagent.com/job_taste-turkey-1/artifacts/244h1mln_image.png', // Хранителни продукти - fridge
                                'cat_59e596f6b3e6': 'https://customer-assets.emergentagent.com/job_taste-turkey-1/artifacts/gmr1zk29_image.png', // Консеривирани храни - shelves with jars
                                'cat_22b1ec9748cf': 'https://customer-assets.emergentagent.com/job_taste-turkey-1/artifacts/2pzsklm1_image.png', // Турски сладки и деликатеси - shelves with Ziya Dede/Berk/Rize
                                'cat_80efdcff717b': 'https://customer-assets.emergentagent.com/job_taste-turkey-1/artifacts/d6pgacft_image.png', // Основни продукти - shelves with flour/rice/pasta
                                'cat_f944f1e80087': 'https://customer-assets.emergentagent.com/job_taste-turkey-1/artifacts/vpmmpo03_image.png', // Ядки - shelves with nuts and snacks
                                'cat_628476f6059a': 'https://customer-assets.emergentagent.com/job_taste-turkey-1/artifacts/vqodbfv5_image.png', // Сосове и добавки - shelves with oils/vinegar/sauces
                                'cat_0538d903de81': 'https://customer-assets.emergentagent.com/job_taste-turkey-1/artifacts/y1zzhxam_image.png', // Перилни и почистващи препарати - shelves with detergents
                                'cat_fc47b169c76b': 'https://customer-assets.emergentagent.com/job_taste-turkey-1/artifacts/biczbwz9_image.png', // Съдове - dishwashing products (Finish, Pril, Pink Stuff)
                                'cat_3cd805345aa2': 'https://customer-assets.emergentagent.com/job_taste-turkey-1/artifacts/qjtysfqo_image.png', // Почистваши зони - cleaning products (Fairy, Vanish, Ariel)
                                'cat_61467c2f332f': 'https://customer-assets.emergentagent.com/job_taste-turkey-1/artifacts/xy7er48r_image.png', // Напитки - Pepsi/CocaCola/Uludag/Frutti drinks
                                'cat_5c552cf85b3e': 'https://customer-assets.emergentagent.com/job_taste-turkey-1/artifacts/kybgl5gf_image.png', // Домакинство - shelves with cleaning products (Fairy, Domestos, Pril)
                                'cat_eed64d30f414': 'https://customer-assets.emergentagent.com/job_taste-turkey-1/artifacts/5msf4b1d_image.png', // Хранителни добавки - shelves with herbal supplements, teas, vitamins
                            };
                            // Name-based fallbacks (work across environments where category_id differs)
                            const categoryNameFallbacks = [
                                { match: 'баклава', url: '/images/baklava-category.jpg' }, // Баклава и халва - user collage photo
                                { match: 'козметика', url: 'https://customer-assets.emergentagent.com/job_taste-turkey-1/artifacts/mkfcbnru_image.png' }, // Козметика - shelf with shampoos
                                { match: 'pink stuff', url: '/images/pink-stuff-category.jpg' }, // The Pink Stuff - merged two shelf photos
                            ];
                            const nameFallback = categoryNameFallbacks.find(f => (category.name || '').toLowerCase().includes(f.match))?.url;
                            const imageUrl = categoryImageFallbacks[category.category_id] || nameFallback || category.image_url || 'https://images.unsplash.com/photo-1606787366850-de6330128bfc?w=800&q=80';
                            const subcategoriesText = category.subcategories?.slice(0, 6).join(' • ') || '';
                            
                            return (
                                <ScrollReveal key={category.category_id} animation={index % 2 === 0 ? 'fade-left' : 'fade-right'} delay={index * 0.1}>
                                    <Link 
                                        to={`/products?category=${category.category_id}`}
                                        data-testid={`category-${category.slug}`}
                                        className={`card-category group block aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl ${style.shadow} transition-all`}
                                    >
                                        <img 
                                            src={imageUrl}
                                            alt={category.name}
                                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                        />
                                        <div className={`absolute inset-0 bg-gradient-to-t ${style.overlay} to-transparent`} />
                                        
                                        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                                            <span className={`inline-block px-4 py-1.5 bg-gradient-to-r ${style.gradient} text-white text-xs font-bold uppercase tracking-wider rounded-xl mb-3 shadow-lg`}>
                                                {style.badge}
                                            </span>
                                            <h3 className="text-2xl md:text-3xl font-bold mb-2 leading-tight" style={{ color: '#ffffff', textShadow: '2px 2px 4px rgba(0,0,0,0.7)' }}>
                                                {category.name}
                                            </h3>
                                            {subcategoriesText && (
                                                <p className="text-sm md:text-base mb-4 max-w-md line-clamp-2" style={{ color: 'rgba(255,255,255,0.95)' }}>
                                                    {subcategoriesText}
                                                </p>
                                            )}
                                            <span className="inline-flex items-center text-white font-bold uppercase tracking-wider text-xs md:text-sm group-hover:translate-x-3 transition-transform duration-300 bg-white/20 backdrop-blur-sm px-5 py-2.5 rounded-xl">
                                                Разгледай <ArrowRight className="ml-2 h-4 w-4" />
                                            </span>
                                        </div>
                                    </Link>
                                </ScrollReveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ===== PROMO BANNERS after categories + promo cards ===== */}
            <section className="container-main space-y-6 pb-4">
                <PromoBanners placement="after_categories" />
                <PromoBanners placement="promo_cards" />
            </section>

            {/* ===== PROMOTIONS with warm background ===== */}
            {onSale.length > 0 && (
                <section className="section-spacing relative overflow-hidden">
                    {/* Glass morphism warm background */}
                    <div className="absolute inset-0 bg-gradient-to-br from-orange-100/85 via-amber-50/85 to-orange-100/85 dark:from-slate-800/85 dark:via-slate-900/85 dark:to-slate-800/85 backdrop-blur-[2px]" />
                    
                    <div className="container-main relative z-10">
                        <ScrollReveal animation="fade-up">
                            <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
                                <div>
                                    <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-gradient-to-r from-red-500 to-orange-500 text-white uppercase tracking-[0.15em] text-sm font-bold mb-3">
                                        🔥 Горещи оферти
                                    </span>
                                    <h2 className="text-3xl md:text-4xl font-bold mt-3 bg-gradient-to-r from-red-600 to-orange-600 dark:from-red-400 dark:to-orange-400 bg-clip-text text-transparent">
                                        Промоции
                                    </h2>
                                </div>
                                <Link to="/products?on_sale=true" className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-red-500 to-orange-500 text-white font-bold rounded-xl hover:shadow-lg hover:shadow-red-500/30 transition-all">
                                    Виж всички <ChevronRight className="h-5 w-5" />
                                </Link>
                            </div>
                        </ScrollReveal>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                            {onSale.map((product, index) => (
                                <ScrollReveal key={product.product_id} animation="scale" delay={index * 0.1}>
                                    <ProductCard product={product} />
                                </ScrollReveal>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* ===== FEATURED PRODUCTS ===== */}
            {featuredProducts.length > 0 && (
                <section className="section-spacing relative overflow-hidden">
                    {/* Glass morphism white background */}
                    <div className="absolute inset-0 bg-white/85 dark:bg-slate-900/85 backdrop-blur-[2px]" />
                    
                    <div className="container-main relative z-10">
                        <ScrollReveal animation="fade-up">
                            <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
                                <div>
                                    <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-gradient-to-r from-amber-100 to-yellow-100 dark:from-amber-900/50 dark:to-yellow-900/50 text-amber-700 dark:text-amber-300 uppercase tracking-[0.15em] text-sm font-bold mb-3">
                                        ⭐ Топ избор
                                    </span>
                                    <h2 className="text-3xl md:text-4xl font-bold mt-3 bg-gradient-to-r from-amber-600 to-orange-600 dark:from-amber-400 dark:to-orange-400 bg-clip-text text-transparent">
                                        Препоръчани продукти
                                    </h2>
                                </div>
                                <Link to="/products?featured=true" className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold rounded-xl hover:shadow-lg hover:shadow-amber-500/30 transition-all">
                                    Виж всички <ChevronRight className="h-5 w-5" />
                                </Link>
                            </div>
                        </ScrollReveal>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                            {featuredProducts.map((product, index) => (
                                <ScrollReveal key={product.product_id} animation="scale" delay={index * 0.1}>
                                    <ProductCard product={product} />
                                </ScrollReveal>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* ===== BESTSELLERS with Turkish sweets background ===== */}
            {bestsellers.length > 0 && (
                <section className="section-spacing relative overflow-hidden">
                    {/* Dark glass morphism - baklava shows through darkened */}
                    <div className="absolute inset-0 bg-amber-950/80 backdrop-blur-[2px]" />
                    
                    <div className="container-main relative z-10">
                        <ScrollReveal animation="fade-up">
                            <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
                                <div>
                                    <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/10 backdrop-blur-md text-amber-300 uppercase tracking-[0.15em] text-sm font-bold mb-3 border border-amber-400/30">
                                        🏆 Бестселъри
                                    </span>
                                    <h2 className="text-3xl md:text-4xl font-bold mt-3 text-white">
                                        Най-търсени
                                    </h2>
                                </div>
                                <Link to="/products?bestseller=true" className="inline-flex items-center gap-2 px-6 py-3 bg-white text-amber-700 font-bold rounded-xl hover:bg-amber-50 transition-all">
                                    Виж всички <ChevronRight className="h-5 w-5" />
                                </Link>
                            </div>
                        </ScrollReveal>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                            {bestsellers.map((product, index) => (
                                <ScrollReveal key={product.product_id} animation="scale" delay={index * 0.1}>
                                    <ProductCard product={product} />
                                </ScrollReveal>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* ===== DELIVERY BANNER with warm theme ===== */}
            <ScrollReveal animation="fade-up">
                <section className="py-20 relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-amber-600/95 via-orange-500/95 to-amber-600/95 backdrop-blur-sm" />
                    {/* Decorative elements */}
                    <div className="absolute inset-0">
                        <div className="absolute top-0 left-0 w-40 h-40 bg-white/10 rounded-full blur-3xl" />
                        <div className="absolute bottom-0 right-0 w-60 h-60 bg-white/10 rounded-full blur-3xl" />
                    </div>
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-white/50 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-white/50 to-transparent" />
                    
                    <div className="container-main relative z-10">
                        <div className="flex flex-col lg:flex-row items-center justify-between gap-10">
                            <div className="flex items-center gap-6">
                                <div className="w-20 h-20 rounded-2xl flex items-center justify-center bg-white/20 backdrop-blur-sm shadow-2xl">
                                    <Truck className="h-10 w-10 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-3xl md:text-4xl font-bold text-white mb-2">
                                        Доставка в цяла България
                                    </h3>
                                    <p className="text-amber-100 text-lg">
                                        Бърза и сигурна доставка с Еконт — до офис или до адрес
                                    </p>
                                </div>
                            </div>
                            <Link to="/products">
                                <Button data-testid="delivery-info-btn" className="bg-white text-amber-700 hover:bg-amber-50 font-bold text-lg px-10 py-4 rounded-xl shadow-xl hover:shadow-2xl transition-all">
                                    Разгледай продуктите
                                    <ArrowRight className="ml-2 h-5 w-5" />
                                </Button>
                            </Link>
                        </div>
                    </div>
                </section>
            </ScrollReveal>

            {/* ===== CONTACT SECTION with warm accents ===== */}
            <ScrollReveal animation="scale">
                <section className="py-20 relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-b from-amber-50/85 to-white/90 backdrop-blur-[2px]" />
                    
                    <div className="container-main relative z-10">
                        <div className="text-center mb-12">
                            <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-gradient-to-r from-amber-100 to-orange-100 text-amber-700 uppercase tracking-[0.15em] text-sm font-bold mb-3">
                                Контакти
                            </span>
                            <h2 className="text-3xl md:text-4xl font-bold mt-4 bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                                Свържете се с нас
                            </h2>
                        </div>
                        
                        <div className="flex flex-col md:flex-row items-center justify-center gap-8">
                            {/* Phone */}
                            <div className="flex items-center gap-5 bg-white px-8 py-6 rounded-2xl shadow-xl hover:shadow-2xl hover:shadow-amber-500/20 transition-all border border-amber-100">
                                <div className="w-16 h-16 rounded-2xl flex items-center justify-center bg-gradient-to-br from-amber-400 to-orange-500 shadow-lg">
                                    <Phone className="h-7 w-7 text-white" />
                                </div>
                                <div className="text-left">
                                    <p className="text-slate-500 text-sm font-medium">Телефон</p>
                                    <a href="tel:0899972727" className="text-2xl font-bold text-slate-800 hover:text-amber-600 transition-colors">
                                        0899 97 27 27
                                    </a>
                                </div>
                            </div>
                            
                            {/* Address */}
                            <div className="flex items-center gap-5 bg-white px-8 py-6 rounded-2xl shadow-xl hover:shadow-2xl hover:shadow-orange-500/20 transition-all border border-orange-100">
                                <div className="w-16 h-16 rounded-2xl flex items-center justify-center bg-gradient-to-br from-orange-400 to-red-500 shadow-lg">
                                    <MapPin className="h-7 w-7 text-white" />
                                </div>
                                <div className="text-left">
                                    <p className="text-slate-500 text-sm font-medium">Адрес</p>
                                    <p className="text-xl font-bold text-slate-800">
                                        гр. София, ул. Пиротска 85
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </ScrollReveal>
            </div> {/* End of content wrapper */}
        </div>
    );
};

export default HomePage;
